import {
  SyntheticEvent,
  UIEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import {
  CheckoutConfigurationType,
  LocalStorageKeys,
  TranslationContext,
} from "@is/constants";
import { ViewStateContext } from "@is/contexts";
import { useWidget } from "@is/hooks";
import { useManageBasket } from "@is/hooks/basket";
import { useManageCheckoutConfigurations } from "@is/hooks/checkout";
import { LocalStorageService } from "@is/services";
import { TrackingEventTypes, trackEvent } from "@is/services/EventTracking";
import { WidgetItem, WidgetType } from "@is/types";
import { createWidgetItem } from "@is/utils";

type Props = {
  isOpen: boolean;
  onGoToCheckout: (
    checkoutConfigurationsType: CheckoutConfigurationType
  ) => void;
};

const useManageBasketDrawerContent = ({ isOpen, onGoToCheckout }: Props) => {
  const { t } = useTranslation(TranslationContext.Basket);
  const { searchTerms, restoredParkedBasketName } =
    useContext(ViewStateContext).content;

  const [basketWidget, setBasketWidget] = useState<WidgetItem>(null);

  const [isScrolled, setIsScrolled] = useState(false);
  const [isParkBasketModalOpen, setIsParkBasketModalOpen] = useState(false);
  const [isFailedModalOpen, setIsFailedModalOpen] = useState(false);
  const [isCheckoutDisabled, setIsCheckoutDisabled] = useState(false);

  const { basketItems } = useManageBasket();
  const [, getWidget] = useWidget();

  const { checkoutConfigurations } = useManageCheckoutConfigurations();

  const enabledCheckoutConfigurations = useMemo(
    () =>
      checkoutConfigurations?.filter(
        (checkoutConfiguration) => checkoutConfiguration.isEnabled
      ),
    [checkoutConfigurations]
  );

  const enabledCheckoutConfigurationsWithoutQRCode = useMemo(
    () =>
      enabledCheckoutConfigurations?.filter(
        (configuration) =>
          configuration.type !== CheckoutConfigurationType.QRCode
      ),
    [enabledCheckoutConfigurations]
  );

  const enabledQRCodeCheckoutConfiguration = useMemo(
    () =>
      enabledCheckoutConfigurations?.find(
        (configuration) =>
          configuration.type === CheckoutConfigurationType.QRCode
      ),
    [enabledCheckoutConfigurations]
  );

  const primaryAttributes = useMemo(
    () => searchTerms.filter((term) => term.isPrimary),
    [searchTerms]
  );

  useEffect(() => {
    if (basketItems?.length && isOpen) {
      const fetchBasketWidget = async () => {
        const variants = basketItems.map((item) => ({
          styleId: item.productId,
          variantId: item.variantId,
        }));

        const widgetItems = await getWidget({
          type: WidgetType.HowAboutThese,
          variants,
          ...(primaryAttributes?.length && { filters: primaryAttributes }),
        });

        setBasketWidget(
          createWidgetItem({
            type: WidgetType.HowAboutThese,
            items: widgetItems,
          })
        );
      };

      fetchBasketWidget();
    }
  }, [basketItems, isOpen, primaryAttributes]);

  const handleShowMoreWidgetItems = useCallback(
    () => setBasketWidget({ ...basketWidget, isExpanded: true }),
    [basketWidget]
  );

  const handleShowAllWidgetItems = useCallback(
    () => setBasketWidget({ ...basketWidget, showAllItems: true }),
    [basketWidget]
  );

  const handleScroll = useCallback((event: UIEvent<HTMLDivElement>) => {
    const { scrollTop } = event.currentTarget;
    setIsScrolled(scrollTop > 0);
  }, []);

  const handleOpenParkBasketModal = useCallback(() => {
    const sessionId = LocalStorageService.getItem(LocalStorageKeys.SessionId);

    setIsParkBasketModalOpen(true);

    trackEvent({
      eventType: TrackingEventTypes.ParkBasketModalOpen,
      data: {
        sessionId,
        items: basketItems.map((item) => ({
          ean: item.ean,
          variantId: item.variantId,
        })),
      },
    });
  }, [basketItems]);

  const handleOpenFailedModal = useCallback(
    () => setIsFailedModalOpen(true),
    []
  );

  const handleGoToCheckout = useCallback(
    (e: SyntheticEvent) => {
      const checkoutConfigurationsType = e.currentTarget.getAttribute("name");

      const storedCheckoutInfo = JSON.parse(
        LocalStorageService.getItem(LocalStorageKeys.CheckoutInfo)
      );

      if (
        storedCheckoutInfo &&
        !storedCheckoutInfo[checkoutConfigurationsType]
      ) {
        LocalStorageService.removeItem(LocalStorageKeys.CheckoutInfo);
        LocalStorageService.removeItem(LocalStorageKeys.PhoneCountryCode);
      }

      onGoToCheckout(checkoutConfigurationsType as CheckoutConfigurationType);
    },
    [onGoToCheckout]
  );

  return {
    basketItems,
    isScrolled,
    isCheckoutDisabled,
    basketWidget,
    handleShowMoreWidgetItems,
    handleShowAllWidgetItems,
    handleScroll,
    setIsCheckoutDisabled,
    checkoutConfigurations: enabledCheckoutConfigurationsWithoutQRCode,
    handleGoToCheckout,
    enabledQRCodeCheckoutConfiguration,
    isParkBasketModalOpen,
    setIsParkBasketModalOpen,
    handleOpenParkBasketModal,
    isFailedModalOpen,
    setIsFailedModalOpen,
    handleOpenFailedModal,
    restoredParkedBasketName,
    t,
  };
};

export default useManageBasketDrawerContent;
